import DualloModelVersionClient from "@/clients/duallo/DualloModelVersionClient";
import DualloScanClient from "@/clients/duallo/DualloScanClient";
import DualloFramesClient from "@/clients/duallo/DualloFramesClient";
import ScanUpdateRequest from "@/domain/scan/ScanUpdateRequest";
import { uploadToAzureBlobStorage } from "@/utils/blobStorageUtil";
import { ApiError } from "@/clients/ClientBase";

class ModelVersionService {
    async getFramesByCurrentModelVersion(assetId){
        const modelVersion = await DualloModelVersionClient.getCurrentModelVersionByAssetId(assetId);
        return await DualloFramesClient.getFramesByScanId(modelVersion.associatedScan);
    }

    async getCurrentModelVersion(assetId){
        // An asset that hasn't been scanned/processed yet has no current model version. The API
        // reports that as a 404; treat it as the legitimate "no version yet" state (null) rather
        // than an error, so callers can branch on null without logging erroneous console output.
        try {
            return await DualloModelVersionClient.getCurrentModelVersionByAssetId(assetId);
        } catch (error) {
            if (error instanceof ApiError && error.status === 404) return null;
            throw error;
        }
    }

    async getModelVersionsByAssetId(assetId){
        return await DualloModelVersionClient.getModelVersionsByAssetId(assetId);
    }

    async getScansByAssetId(assetId){
        return await DualloScanClient.getScansByAssetId(assetId);
    }

    async getFramesByScanId(scanId){
        return await DualloFramesClient.getFramesByScanId(scanId); 
    }

    async getScanById(assetId, scanId){
        return await DualloScanClient.getScanById(assetId, scanId);
    }

    async createScanRecord(createScanRequest){
        return await DualloScanClient.createScanRecord(createScanRequest);
    }

    async createNewModelVersionRecord(createVersionRequest){
        return await DualloModelVersionClient.createNewModelVersion(createVersionRequest);
    }

    async updateModelVersion(updateRecord){
        return await DualloModelVersionClient.updateModelVersion(updateRecord);
    }

    async updateModelVersionScans(updateRecord){
        return await DualloModelVersionClient.updateModelVersionScans(updateRecord);
    }

    async setCurrentModelVersion(assetId, versionId){
        return await DualloModelVersionClient.setCurrentModelVersion(assetId, versionId);
    }

    async publishModelVersion(orgId, assetId, versionId){
        return await DualloModelVersionClient.publishModelVersion(orgId, assetId, versionId);
    }

    async deleteModelVersion(assetId, versionId, softDelete = true){
        return await DualloModelVersionClient.deleteModelVersion(assetId, versionId, softDelete);
    }

    async uploadVideoToScan(request){
        return await DualloScanClient.uploadVideoToScan(request);
    }

    async startScanGeneration(startRequest){
        return await DualloScanClient.startScanGeneration(startRequest);
    }

    async uploadScanFileUsingSaSLink(uploadUrl, file, contentType, onProgress = null){
        return await uploadToAzureBlobStorage(uploadUrl, file, contentType, onProgress);
    }

    async updateScanSourceFileUploadStatus(assetId, scanId, success, videoLengthSeconds = 0){
        const update = new ScanUpdateRequest(assetId, scanId, success, videoLengthSeconds);
        return await DualloScanClient.updateScanSourceFileUploadStatus(update);
    }

    async updateScanPerimeter(assetId, scanId, perimeterJson){
        return await DualloScanClient.updateScanPerimeter(assetId, scanId, perimeterJson);
    }
}

export default new ModelVersionService();